/**
 * @file footer-menu-actions.ts
 * @description «⋯» в «Меню → Подвал» колонками (phase19.16, приёмка Р2а;
 * SB113 П3 — в подвале нет пунктов вне колонки; uiux-footer-columns §4.6):
 *   - у пункта: Изменить · Выше · Ниже · Перенести в колонку «…» · Скрыть
 *     · Удалить («Вложить / Вынести» в подвале нет: пункт всегда в колонке);
 *   - у колонки: Левее · Правее · Скрыть / Показать · Удалить колонку.
 * «Перенести в колонку» — по строке на каждую другую колонку из пунктов
 * (в автосписок категорий не переносят): это и есть путь без перетаскивания
 * для касания и клавиатуры.
 */

import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Eye, EyeOff, MoveRight, Pencil, Trash2 } from "lucide-react";

import type { RowAction } from "../../ui/RowActionsMenu";
import { locate, moveBy, moveToColumn } from "../../../lib/site-view/menu-tree";
import type { SiteMenuItem } from "../../../lib/site-view/types";

export interface FooterMenuHandlers {
  edit: (item: SiteMenuItem) => void;
  reorder: (tree: SiteMenuItem[]) => void;
  toggleHidden: (item: SiteMenuItem) => void;
  remove: (item: SiteMenuItem) => void;
  removeColumn: (column: SiteMenuItem) => void;
}

/** Подпись колонки в «Перенести в …». */
export function columnName(column: SiteMenuItem, index: number): string {
  return column.label?.trim() ? `«${column.label.trim()}»` : `${index + 2} (без заголовка)`;
}

const hideAction = (item: SiteMenuItem, h: FooterMenuHandlers): RowAction =>
  item.isHidden
    ? { key: "show", label: "Показать на сайте", icon: Eye, run: () => h.toggleHidden(item) }
    : { key: "hide", label: "Скрыть с сайта", icon: EyeOff, run: () => h.toggleHidden(item) };

export function footerItemActions(tree: SiteMenuItem[], item: SiteMenuItem, h: FooterMenuHandlers): RowAction[] {
  const at = locate(tree, item.id);
  const siblings = at && at.parent >= 0 ? tree[at.parent]!.children.length : 0;
  const apply = (next: SiteMenuItem[] | null) => () => {
    if (next) h.reorder(next);
  };
  const moves: RowAction[] = tree.flatMap((column, index) =>
    column.linkType === "GROUP" && at && index !== at.parent
      ? [{ key: `to:${column.id}`, label: `Перенести в колонку ${columnName(column, index)}`, icon: MoveRight, run: apply(moveToColumn(tree, item.id, column.id)) }]
      : [],
  );
  return [
    { key: "edit", label: "Изменить", icon: Pencil, run: () => h.edit(item) },
    { key: "up", label: "Выше", icon: ArrowUp, disabled: !at || at.index === 0, run: apply(moveBy(tree, item.id, -1)) },
    { key: "down", label: "Ниже", icon: ArrowDown, disabled: !at || at.index >= siblings - 1, run: apply(moveBy(tree, item.id, 1)) },
    ...moves,
    hideAction(item, h),
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => h.remove(item) },
  ];
}

export function footerColumnActions(tree: SiteMenuItem[], column: SiteMenuItem, h: FooterMenuHandlers): RowAction[] {
  const index = tree.findIndex((c) => c.id === column.id);
  const apply = (next: SiteMenuItem[] | null) => () => {
    if (next) h.reorder(next);
  };
  return [
    { key: "left", label: "Левее", icon: ArrowLeft, disabled: index <= 0, run: apply(moveBy(tree, column.id, -1)) },
    { key: "right", label: "Правее", icon: ArrowRight, disabled: index < 0 || index >= tree.length - 1, run: apply(moveBy(tree, column.id, 1)) },
    hideAction(column, h),
    { key: "delete", label: "Удалить колонку", icon: Trash2, danger: true, run: () => h.removeColumn(column) },
  ];
}
